import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { updateLead } from '@/api/leads.api';
import { ApiError } from '@/api/errors';
import { formatDateTime } from '@/lib/format';
import type { LeadResponse } from '@/types/shared';
import './LeadPanels.css';

interface LeadNotesPanelProps {
  lead: LeadResponse;
  canEdit: boolean;
  onSaved: (updated: LeadResponse) => void;
}

/** Append-only notes log (`notesLog` — never edited/removed, only ever appended to) plus its own
 * small "add a note" form, saved independently via `PATCH /leads/{id}` (`appendNote`). */
export function LeadNotesPanel({ lead, canEdit, onSaved }: LeadNotesPanelProps) {
  const { t, i18n } = useTranslation();
  const [draft, setDraft] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleAddNote() {
    const text = draft.trim();
    if (!text) return;
    setSubmitting(true);
    setError(null);
    try {
      const updated = await updateLead(lead.leadId, { appendNote: text });
      onSaved(updated);
      setDraft('');
    } catch (err) {
      const apiError = ApiError.fromUnknown(err);
      setError(apiError.message || t('leads.detail.errors.addNoteFailed'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <section className="fa-form-section fa-lead-panel">
      <h2 className="fa-form-section__title">{t('leads.detail.notesTitle')}</h2>

      {lead.notesLog.length === 0 ? (
        <p className="fa-field__hint">{t('leads.detail.notesEmpty')}</p>
      ) : (
        <ul className="fa-lead-notes">
          {lead.notesLog
            .slice()
            .reverse()
            .map((note, idx) => (
              <li key={idx} className="fa-lead-notes__item">
                <div className="fa-lead-notes__meta">
                  <span className="fa-lead-notes__author">
                    {note.author?.fullName ?? t('leads.unknownUser')}
                  </span>
                  <bdi className="fa-lead-notes__date">
                    {formatDateTime(note.timestamp, i18n.language)}
                  </bdi>
                </div>
                <p className="fa-lead-notes__text">{note.text}</p>
              </li>
            ))}
        </ul>
      )}

      {canEdit && (
        <div className="fa-lead-panel__add-note">
          <label htmlFor="lead-newNote" className="fa-visually-hidden">
            {t('leads.detail.addNotePlaceholder')}
          </label>
          <textarea
            id="lead-newNote"
            placeholder={t('leads.detail.addNotePlaceholder')}
            value={draft}
            disabled={submitting}
            onChange={(e) => setDraft(e.target.value)}
          />
          <div className="fa-lead-panel__actions">
            <button
              type="button"
              className="fa-btn fa-btn--secondary"
              disabled={!draft.trim() || submitting}
              onClick={handleAddNote}
            >
              {submitting ? t('leads.detail.addingNote') : t('leads.detail.addNoteSubmit')}
            </button>
            {error && (
              <p className="fa-field__error" role="alert">
                {error}
              </p>
            )}
          </div>
        </div>
      )}
    </section>
  );
}
